import React, { useEffect, useState } from 'react';
import { Building2, Globe, DollarSign, Users, AlertTriangle, X, Pencil } from 'lucide-react';
import { Button } from '../UI/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../UI/dialog';
import { useLocale } from '../../contexts/LocaleContext';
import {
  SUPPLIER_INDUSTRIES,
  SUPPLIER_RISK_CATEGORIES,
  SUPPLIER_TIERS,
  companyToSupplierForm,
  supplierFormToUpdatePayload,
} from '../../config/supplierFormConfig';

const EditCompanyModal = ({ company, open, onClose, onSubmit, userScoped = false }) => {
  const { t } = useLocale();
  const [formData, setFormData] = useState(() => companyToSupplierForm(company));
  const [keywordInput, setKeywordInput] = useState('');
  const [riskInput, setRiskInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open && company) {
      setFormData(companyToSupplierForm(company));
      setErrors({});
    }
  }, [open, company]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const addKeyword = () => {
    if (keywordInput.trim() && !formData.keywords.includes(keywordInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        keywords: [...prev.keywords, keywordInput.trim()],
      }));
      setKeywordInput('');
    }
  };

  const removeKeyword = (keyword) => {
    setFormData((prev) => ({
      ...prev,
      keywords: prev.keywords.filter((k) => k !== keyword),
    }));
  };

  const addRisk = () => {
    if (riskInput.trim() && !formData.supplyChainRisks.includes(riskInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        supplyChainRisks: [...prev.supplyChainRisks, riskInput.trim()],
      }));
      setRiskInput('');
    }
  };

  const removeRisk = (risk) => {
    setFormData((prev) => ({
      ...prev,
      supplyChainRisks: prev.supplyChainRisks.filter((r) => r !== risk),
    }));
  };

  const toggleRiskCategory = (category) => {
    setFormData((prev) => ({
      ...prev,
      riskCategories: prev.riskCategories.includes(category)
        ? prev.riskCategories.filter((c) => c !== category)
        : [...prev.riskCategories, category],
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.ticker.trim()) newErrors.ticker = t('createSupplierTickerRequired');
    if (!formData.name.trim()) newErrors.name = t('createSupplierNameRequired');
    if (!formData.industry.trim()) newErrors.industry = t('createSupplierIndustryRequired');
    if (!formData.description.trim()) newErrors.description = t('createSupplierDescriptionRequired');
    if (formData.website.trim()) {
      try {
        // eslint-disable-next-line no-new
        new URL(formData.website.startsWith('http') ? formData.website : `https://${formData.website}`);
      } catch {
        newErrors.website = t('createSupplierWebsiteInvalid');
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      await onSubmit(
        supplierFormToUpdatePayload(
          formData,
          company.ticker || company.companyId || company.company_id
        )
      );
    } finally {
      setLoading(false);
    }
  };

  if (!open || !company) return null;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-y-auto gap-0 p-0 border-t-4 border-t-primary-500"
        closeLabel={t('dialogCloseLabel')}
      >
        <DialogHeader className="sticky top-0 z-10 border-b border-border-light bg-background-card px-8 py-6 pr-14">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-100">
              <Pencil className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <DialogTitle className="text-2xl">{t('editSupplierTitle')}</DialogTitle>
              <DialogDescription>
                {userScoped
                  ? t('editSupplierSubtitleUserScoped', { name: company.name })
                  : t('editSupplierSubtitle', { name: company.name })}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="p-8 space-y-8">
          <div>
            <h3 className="text-lg font-semibold mb-4 text-text-primary">
              {t('createSupplierSectionBasic')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2 text-text-secondary">
                  {t('createSupplierTickerLabel')}
                </label>
                <input
                  type="text"
                  name="ticker"
                  value={formData.ticker}
                  readOnly
                  className="input opacity-70 cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-text-secondary">
                  {t('createSupplierNameLabel')} *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  maxLength={255}
                  className={`input ${errors.name ? 'border-red-500' : ''}`}
                />
                {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-text-secondary">
                  {t('createSupplierIndustryLabel')} *
                </label>
                <select
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                  className={`input ${errors.industry ? 'border-red-500' : ''}`}
                >
                  <option value="">{t('createSupplierIndustryPlaceholder')}</option>
                  {SUPPLIER_INDUSTRIES.map((ind) => (
                    <option key={ind.value} value={ind.value}>
                      {t(ind.labelKey)}
                    </option>
                  ))}
                </select>
                {errors.industry && <p className="mt-1 text-sm text-red-600">{errors.industry}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-text-secondary">
                  {t('createSupplierTierLabel')} *
                </label>
                <select name="tier" value={formData.tier} onChange={handleChange} className="input">
                  {SUPPLIER_TIERS.map((tier) => (
                    <option key={tier} value={tier}>
                      {t(`createSupplierTier${tier}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-text-secondary">
                  {t('createSupplierSectorLabel')}
                </label>
                <input
                  type="text"
                  name="sector"
                  value={formData.sector}
                  onChange={handleChange}
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-text-secondary">
                  {t('createSupplierCountryLabel')}
                </label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-text-secondary">
                  <Globe className="inline h-4 w-4 mr-1" />
                  {t('createSupplierWebsiteLabel')}
                </label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  className={`input ${errors.website ? 'border-red-500' : ''}`}
                />
                {errors.website && <p className="mt-1 text-sm text-red-600">{errors.website}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-text-secondary">
                  <DollarSign className="inline h-4 w-4 mr-1" />
                  {t('createSupplierMarketCapLabel')}
                </label>
                <input
                  type="number"
                  name="marketCap"
                  value={formData.marketCap}
                  onChange={handleChange}
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-text-secondary">
                  <Users className="inline h-4 w-4 mr-1" />
                  {t('createSupplierEmployeeCountLabel')}
                </label>
                <input
                  type="number"
                  name="employeeCount"
                  value={formData.employeeCount}
                  onChange={handleChange}
                  className="input"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-text-secondary">
              {t('createSupplierDescriptionLabel')} *
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              maxLength={1000}
              rows={4}
              className={`input ${errors.description ? 'border-red-500' : ''}`}
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">{errors.description}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-text-secondary">
              {t('createSupplierKeywordsLabel')}
            </label>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addKeyword();
                  }
                }}
                placeholder={t('createSupplierKeywordsPlaceholder')}
                className="input flex-1"
              />
              <Button type="button" variant="secondary" onClick={addKeyword}>
                {t('createSupplierAddButton')}
              </Button>
            </div>
            {formData.keywords.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.keywords.map((keyword) => (
                  <span
                    key={keyword}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-700"
                  >
                    {keyword}
                    <button
                      type="button"
                      onClick={() => removeKeyword(keyword)}
                      className="ml-2 hover:text-danger-500"
                      aria-label={t('remove')}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-3 text-text-secondary">
              <AlertTriangle className="inline h-4 w-4 mr-1" />
              {t('createSupplierRiskCategoriesLabel')}
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {SUPPLIER_RISK_CATEGORIES.map((category) => (
                <label
                  key={category.value}
                  className="flex items-center space-x-2 p-3 rounded-lg border-2 cursor-pointer transition-all border-border-light has-[:checked]:border-primary-500 has-[:checked]:bg-primary-50"
                >
                  <input
                    type="checkbox"
                    checked={formData.riskCategories.includes(category.value)}
                    onChange={() => toggleRiskCategory(category.value)}
                    className="rounded accent-primary-500"
                  />
                  <span className="text-sm font-medium text-text-secondary">
                    {t(category.labelKey)}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-text-secondary">
              {t('createSupplierSupplyChainRisksLabel')}
            </label>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={riskInput}
                onChange={(e) => setRiskInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addRisk();
                  }
                }}
                placeholder={t('createSupplierSupplyChainRisksPlaceholder')}
                className="input flex-1"
              />
              <Button type="button" variant="secondary" onClick={addRisk}>
                {t('createSupplierAddButton')}
              </Button>
            </div>
            {formData.supplyChainRisks.length > 0 && (
              <div className="space-y-2">
                {formData.supplyChainRisks.map((risk) => (
                  <div
                    key={risk}
                    className="flex items-start justify-between p-3 rounded-lg bg-warning-50"
                  >
                    <span className="text-sm text-text-secondary">{risk}</span>
                    <button
                      type="button"
                      onClick={() => removeRisk(risk)}
                      className="text-danger-500 hover:text-danger-600 ml-2"
                      aria-label={t('remove')}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 pt-6 border-t border-border-light">
            <Button type="button" variant="ghost" onClick={onClose} disabled={loading}>
              {t('cancel')}
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? t('editSupplierSaving') : t('editSupplierSubmit')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditCompanyModal;
